import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowRight, Image as ImageIcon, Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const [postsRes, featuredRes] = await Promise.all([
        axios.get(`${API}/blog/posts${params}`),
        axios.get(`${API}/blog/posts?featured=true`)
      ]);
      setPosts(postsRes.data.posts);
      setFeaturedPosts(featuredRes.data.posts.slice(0, 3));
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/blog/categories`);
      setCategories(res.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-void-black min-h-screen">
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" className="font-syne font-extrabold text-xl tracking-tighter uppercase">
            <span className="text-neon-red">Shadowcell</span> Studios
          </Link>
          <div className="flex items-center gap-6">
            <Link 
              to="/"
              className="font-manrope font-medium text-sm tracking-widest uppercase text-neutral-300 hover:text-neon-red transition-colors"
            >
              Home
            </Link>
            <Link 
              to="/blog"
              className="font-manrope font-medium text-sm tracking-widest uppercase text-neon-red"
            >
              Blog
            </Link>
            <Link 
              to="/admin/login"
              className="font-manrope font-medium text-sm tracking-widest uppercase text-neutral-300 hover:text-neon-red transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-syne font-bold text-4xl md:text-6xl text-white uppercase tracking-tight">
              Blog & <span className="text-neon-red">News</span>
            </h1>
            <p className="font-manrope text-neutral-400 mt-4 max-w-2xl mx-auto">
              Behind the scenes, workflow insights, and the latest from ShadowCell Studios.
            </p>
          </motion.div>

          {/* Categories */}
          {categories.length > 0 && (
            <motion.div 
              className="flex flex-wrap justify-center gap-3 mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
                  !selectedCategory 
                    ? 'bg-neon-red text-white' 
                    : 'bg-white/5 text-neutral-400 hover:bg-white/10'
                }`}
              >
                All Posts
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full font-manrope text-sm transition-all ${
                    selectedCategory === cat.slug 
                      ? 'bg-neon-red text-white' 
                      : 'bg-white/5 text-neutral-400 hover:bg-white/10'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </motion.div>
          )}

          {/* Featured Posts */}
          {featuredPosts.length > 0 && !selectedCategory && (
            <motion.div 
              className="mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="font-syne font-bold text-2xl text-white mb-8 flex items-center gap-2">
                <span className="text-neon-red">★</span> Featured
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {featuredPosts.map((post) => (
                  <Link 
                    key={post.id}
                    to={`/blog/${post.id}`}
                    className="group"
                  >
                    <div className="glass rounded-lg overflow-hidden border border-neon-red/30 hover:border-neon-red transition-all">
                      {post.featured_image ? (
                        <div className="aspect-video relative overflow-hidden">
                          <img 
                            src={post.featured_image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video bg-neon-red/10 flex items-center justify-center">
                          <ImageIcon className="w-12 h-12 text-neon-red/30" />
                        </div>
                      )}
                      <div className="p-6">
                        <h3 className="font-syne font-bold text-lg text-white group-hover:text-neon-red transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="font-manrope text-neutral-400 text-sm mt-2 line-clamp-2">
                          {post.excerpt || post.content.substring(0, 100)}...
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* All Posts */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-neon-red border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : posts.length === 0 ? (
            <motion.div 
              className="text-center py-16 glass rounded-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <ImageIcon className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
              <p className="font-manrope text-neutral-400">No posts yet. Check back soon!</p>
            </motion.div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={`/blog/${post.id}`}
                    className="group block"
                  >
                    <div className="glass rounded-lg overflow-hidden hover:border-white/20 border border-transparent transition-all h-full">
                      {post.featured_image ? (
                        <div className="aspect-video relative overflow-hidden">
                          <img 
                            src={post.featured_image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {post.videos && post.videos.length > 0 && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Play className="w-12 h-12 text-white" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="aspect-video bg-white/5 flex items-center justify-center">
                          <ImageIcon className="w-12 h-12 text-neutral-700" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          {post.category && (
                            <span className="px-2 py-1 bg-neon-red/20 text-neon-red text-xs font-manrope rounded">
                              {post.category}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-neutral-500 text-xs font-manrope">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.created_at)}
                          </span>
                        </div>
                        <h3 className="font-syne font-bold text-lg text-white group-hover:text-neon-red transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="font-manrope text-neutral-400 text-sm mt-2 line-clamp-3">
                          {post.excerpt || post.content.substring(0, 120)}...
                        </p>
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {post.tags.slice(0, 3).map((tag) => (
                              <span 
                                key={tag}
                                className="flex items-center gap-1 text-neutral-500 text-xs font-manrope"
                              >
                                <Tag className="w-3 h-3" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="mt-4 flex items-center gap-2 text-neon-red text-sm font-manrope group-hover:gap-3 transition-all">
                          Read More <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-manrope text-neutral-500 text-sm">
            © {new Date().getFullYear()} ShadowCell Studios. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
